import { Api, Filters as CommonFilterContract } from '@firmable/contracts';
import { CountryNamesByCode } from '../Country';

/**
 * The static filter option lists, by identifier: shared so the web app and the Partner API offer
 * exactly the same codes. Options that live in Search (industry, locations) are fetched from it.
 */

export const download = {
  'cus-downloaded': 'Downloaded records',
};

export const pushedOrEnriched = {
  'cus-pushed-enriched': 'Pushed or enriched records',
};

const companySize = {
  '1': '1 - 10',
  '2': '11 - 50',
  '3': '51 - 250',
  '4': '251 - 500',
  '5': '501 - 1,000',
  '6': '1,001 - 5,000',
  '7': '5,001 - 10,000',
  '8': '10,001+',
  '9': 'No data available',
};

const globalCompanySize = {
  '1': '1 - 10',
  '2': '11 - 50',
  '3': '51 - 250',
  '4': '251 - 500',
  '5': '501 - 1,000',
  '6': '1,001 - 5,000',
  '7': '5,001 - 10,000',
  '8': '10,001+',
  '9': 'No data available',
};

const companyType = {
  '1': 'Associations',
  '2': 'Government',
  '3': 'Individual/sole trader',
  '4': 'Partnership',
  '5': 'Private company',
  '6': 'Public company',
  '7': 'Superannuation fund',
  '8': 'Trust',
  '9': 'No data available',
};

const department = {
  '1': 'General Management',
  '2': 'Sales',
  '3': 'Trades',
  '4': 'Operations',
  '5': 'Engineering & Technical',
  '6': 'Human Resources',
  '7': 'Customer Service',
  '8': 'Medicine & Healthcare',
  '9': 'Research & Analysis',
  '10': 'Legal',
  '11': 'Marketing',
  '12': 'Education & Training',
  '13': 'Consulting',
  '14': 'Finance',
  '15': 'Product',
  '16': 'Other',
  '17': 'No data available',
};

const emailDeliverability = { '1': 'Highly likely', '3': 'Likely' };

const influencer = {
  '1': 'Very High',
  '2': 'High',
  '3': 'Medium',
  '4': 'Low',
  '5': 'Very Low',
};

const revenue = {
  '1': '<$1M',
  '2': '$1M - $5M',
  '3': '$5M - $10M',
  '4': '$10M - $50M',
  '5': '$50M - $100M',
  '6': '$100M - $250M',
  '7': '$250M - $500M',
  '8': '$500M - $1B',
  '9': '>$1B+',
  '10': 'No data available',
};

const reviewsAndRatings = {
  '1': '4.5 and above',
  '2': '4 to 4.5',
  '3': '3 to 4',
  '4': '2 to 3',
  '5': '1 to 2',
  '6': 'Less than 1',
  '7': 'No data available',
};

const seniority = {
  '1': 'Board Member / Company Director',
  '2': 'Owner / Founder',
  '3': 'C-Suite / Partner',
  '4': 'VP/Director / Head of',
  '5': 'Manager',
  '6': 'Other',
  '7': 'No data available',
};

const socialMedia = {
  '1': '0 - 100K',
  '2': '100K - 250K',
  '3': '250K - 500K',
  '4': '500K - 1M',
  '5': '1M - 5M',
  '6': '5M - 10M',
  '7': '10M+',
  '8': 'No data available',
};

const webTraffic = {
  '1': '0 - 100K',
  '2': '100K - 250K',
  '3': '250K - 500K',
  '4': '500K - 1M',
  '5': '1M - 5M',
  '6': '5M - 10M',
  '7': '10M+',
  '8': 'No data available',
};

const currentRoleDuration = {
  '4': 'Last 3 Months',
  '7': 'Last 6 Months',
  '10': 'Last 9 Months',
  '13': 'Last 12 Months',
};

const multipleLocations = {
  '1': '1 location',
  '2': '2 - 5 locations',
  '3': '6 - 10 locations',
  '4': '11 - 20 locations',
  '6': '20+ locations',
  '9': 'No data available',
};

const jobPosts = {
  '4': 'Last 3 months',
  '7': 'Last 6 months',
  '10': 'Last 9 months',
  '13': 'Last 12 months',
};

const b2bVsB2c = {
  '1': 'B2B',
  '2': 'B2C',
  '4': 'No data available',
};

const isMNC = {
  '1': 'Yes',
  '2': 'No',
  '3': 'No data available',
};

const targetMarket = {
  '1': 'Horizontal',
  '2': 'Vertical',
  '3': 'Consumers',
  '4': 'No data available',
};

const yearFounded = {
  '1': 'Before 1900',
  '2': '1900 - 1949',
  '3': '1950 - 1969',
  '4': '1970 - 1979',
  '5': '1980 - 1989',
  '6': '1990 - 1999',
  '7': '2000 - 2009',
  '8': '2010 - 2019',
  '9': '2020 - Present',
  '10': 'No data available',
};

type Identifiers = Exclude<CommonFilterContract.NonSearchable, 'crm_agent' | 'crm_owner'>;

const filterOptions: Record<Identifiers, Api.Search.FilterCommonOptions> = {
  'cus-downloaded': download,
  'cus-pushed-enriched': pushedOrEnriched,
  country: CountryNamesByCode,
  company_size: companySize,
  global_company_size: globalCompanySize,
  company_type: companyType,
  department: department,
  email_deliverability: emailDeliverability,
  influencer: influencer,
  revenue: revenue,
  reviews_and_ratings: reviewsAndRatings,
  seniority: seniority,
  social_media: socialMedia,
  web_traffic: webTraffic,
  current_role_duration: currentRoleDuration,
  multiple_locations: multipleLocations,
  job_posts: jobPosts,
  b2b_vs_b2c: b2bVsB2c,
  is_mnc: isMNC,
  target_market: targetMarket,
  year_founded: yearFounded,
};

export { filterOptions };
